import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getCalcProjectSettings, updateCalcProjectSettings } from "../../api/calc";
import type { CalcSheet } from "../../types/calc";
import { groupCalcSheets, type CalcSheetGroup } from "./calcSheetState";

export type CalcGroupMode = "manual" | "automatic";

export type ManualCalcGroup = {
  id: string;
  label: string;
};

export type CalcGroupingPreferences = {
  mode: CalcGroupMode;
  groups: ManualCalcGroup[];
  assignments: Record<string, string>;
};

const UNGROUPED_ID = "ungrouped";
const STORAGE_PREFIX = "firmark:calc-groups:";
const CHANGE_EVENT = "firmark:calc-groups-change";

const emptyPreferences = (): CalcGroupingPreferences => ({
  mode: "manual",
  groups: [],
  assignments: {},
});

function storageKey(projectId: string): string {
  return `${STORAGE_PREFIX}${projectId}`;
}

function readPreferences(projectId: string): CalcGroupingPreferences {
  if (!projectId || typeof window === "undefined") return emptyPreferences();
  try {
    const raw = window.localStorage.getItem(storageKey(projectId));
    if (!raw) return emptyPreferences();
    const parsed = JSON.parse(raw) as Partial<CalcGroupingPreferences>;
    const groups = Array.isArray(parsed.groups)
      ? parsed.groups.filter(
          (group): group is ManualCalcGroup =>
            !!group && typeof group.id === "string" && typeof group.label === "string",
        )
      : [];
    const assignments = parsed.assignments && typeof parsed.assignments === "object"
      ? Object.fromEntries(
          Object.entries(parsed.assignments).filter(
            ([sheetId, groupId]) => typeof sheetId === "string" && typeof groupId === "string",
          ),
        )
      : {};
    return {
      mode: parsed.mode === "automatic" ? "automatic" : "manual",
      groups,
      assignments,
    };
  } catch {
    return emptyPreferences();
  }
}

function writePreferences(projectId: string, preferences: CalcGroupingPreferences): void {
  if (!projectId || typeof window === "undefined") return;
  window.localStorage.setItem(storageKey(projectId), JSON.stringify(preferences));
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { projectId } }));
}

function hasSavedPreferences(preferences: CalcGroupingPreferences): boolean {
  return preferences.mode !== "manual"
    || preferences.groups.length > 0
    || Object.keys(preferences.assignments).length > 0;
}

export function organizeCalcSheets<T extends CalcSheet>(
  sheets: T[],
  preferences: CalcGroupingPreferences,
  includeEmpty = false,
): CalcSheetGroup<T>[] {
  if (preferences.mode === "automatic") return groupCalcSheets(sheets);

  const knownGroupIds = new Set(preferences.groups.map((group) => group.id));
  const groups = preferences.groups.map((group) => ({
    ...group,
    members: sheets.filter((sheet) => preferences.assignments[sheet.id] === group.id),
  }));
  const ungrouped = sheets.filter((sheet) => {
    const assigned = preferences.assignments[sheet.id];
    return !assigned || !knownGroupIds.has(assigned);
  });
  const visible = includeEmpty ? groups : groups.filter((group) => group.members.length > 0);
  if (ungrouped.length > 0 || (includeEmpty && groups.length === 0)) {
    visible.push({ id: UNGROUPED_ID, label: "Ungrouped", members: ungrouped });
  }
  return visible;
}

export function useCalcGrouping(projectId: string, sheets: CalcSheet[]) {
  const [preferences, setPreferences] = useState<CalcGroupingPreferences>(() =>
    readPreferences(projectId),
  );
  const editRevision = useRef(0);
  const saveQueue = useRef<Promise<unknown>>(Promise.resolve());
  const activeProject = useRef(projectId);
  activeProject.current = projectId;

  useEffect(() => {
    if (!projectId) return;
    let alive = true;
    editRevision.current = 0;
    saveQueue.current = Promise.resolve();
    const hydrationRevision = editRevision.current;
    const cached = readPreferences(projectId);
    setPreferences(cached);
    getCalcProjectSettings(projectId)
      .then(async (response) => {
        if (!alive || editRevision.current !== hydrationRevision) return;
        const persisted = response.grouping;
        // One-time migration for projects that used the former browser-only
        // grouping state. Once the API has data, it is always authoritative.
        if (!response.persisted && hasSavedPreferences(cached)) {
          const migrated = await updateCalcProjectSettings(projectId, {
            persisted: true,
            grouping: cached,
          });
          if (!alive || editRevision.current !== hydrationRevision) return;
          writePreferences(projectId, migrated.grouping);
          setPreferences(migrated.grouping);
          return;
        }
        writePreferences(projectId, persisted);
        setPreferences(persisted);
      })
      .catch(() => {
        // Keep the cache available offline. A later mount retries the API.
      });
    return () => { alive = false; };
  }, [projectId]);

  useEffect(() => {
    const sync = (event: Event) => {
      const changedProject = (event as CustomEvent<{ projectId?: string }>).detail?.projectId;
      if (changedProject === projectId) setPreferences(readPreferences(projectId));
    };
    const syncFromStorage = (event: StorageEvent) => {
      if (event.key === storageKey(projectId)) setPreferences(readPreferences(projectId));
    };
    window.addEventListener(CHANGE_EVENT, sync);
    window.addEventListener("storage", syncFromStorage);
    return () => {
      window.removeEventListener(CHANGE_EVENT, sync);
      window.removeEventListener("storage", syncFromStorage);
    };
  }, [projectId]);

  const commit = useCallback(
    (update: (current: CalcGroupingPreferences) => CalcGroupingPreferences) => {
      // The API is authoritative across browsers; localStorage is the
      // synchronous cache shared by the overview, project nav, and open tabs.
      // Write the complete cache snapshot before broadcasting it locally.
      const next = update(readPreferences(projectId));
      editRevision.current += 1;
      writePreferences(projectId, next);
      setPreferences(next);
      // Preserve interaction order across rapid edits. Without this queue, a
      // slower earlier request could arrive last and overwrite a newer group or
      // assignment on the server.
      saveQueue.current = saveQueue.current
        .catch(() => undefined)
        .then(() => updateCalcProjectSettings(projectId, {
          persisted: true,
          grouping: next,
        }))
        .then((persisted) => {
          // Apply server normalization only if no newer local edit superseded
          // this snapshot while it was in flight.
          if (activeProject.current === projectId
              && JSON.stringify(readPreferences(projectId)) === JSON.stringify(next)) {
            writePreferences(projectId, persisted.grouping);
            setPreferences(persisted.grouping);
          }
        })
        .catch(async () => {
          // Do not leave the browser claiming a save the API rejected. Restore
          // the latest server snapshot when reachable; if offline, the cache
          // remains available and the next mount rehydrates authoritatively.
          try {
            const latest = await getCalcProjectSettings(projectId);
            if (activeProject.current === projectId
                && JSON.stringify(readPreferences(projectId)) === JSON.stringify(next)) {
              writePreferences(projectId, latest.grouping);
              setPreferences(latest.grouping);
            }
          } catch { /* offline cache remains until API access returns */ }
        });
    },
    [projectId],
  );

  const setMode = useCallback(
    (mode: CalcGroupMode) => commit((current) => ({ ...current, mode })),
    [commit],
  );

  const createGroup = useCallback(
    (label: string): string => {
      const id = `group-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
      commit((current) => ({
        ...current,
        groups: [...current.groups, { id, label: label.trim() || "New group" }],
      }));
      return id;
    },
    [commit],
  );

  const renameGroup = useCallback(
    (groupId: string, label: string) => commit((current) => ({
      ...current,
      groups: current.groups.map((group) =>
        group.id === groupId ? { ...group, label: label.trim() || group.label } : group,
      ),
    })),
    [commit],
  );

  const deleteGroup = useCallback(
    (groupId: string) => commit((current) => ({
      ...current,
      groups: current.groups.filter((group) => group.id !== groupId),
      assignments: Object.fromEntries(
        Object.entries(current.assignments).filter(([, assigned]) => assigned !== groupId),
      ),
    })),
    [commit],
  );

  const assignSheet = useCallback(
    (sheetId: string, groupId: string | null) => commit((current) => {
      const assignments = { ...current.assignments };
      if (!groupId || groupId === UNGROUPED_ID) delete assignments[sheetId];
      else assignments[sheetId] = groupId;
      return { ...current, assignments };
    }),
    [commit],
  );

  const groups = useMemo(
    () => organizeCalcSheets(sheets, preferences, true),
    [sheets, preferences],
  );

  return {
    preferences,
    groups,
    setMode,
    createGroup,
    renameGroup,
    deleteGroup,
    assignSheet,
    groupForSheet: (sheetId: string): string => preferences.assignments[sheetId] ?? UNGROUPED_ID,
  };
}

export { UNGROUPED_ID };
